// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import {
    AdvancedTab,
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Clock from "@insite/mobius/Icons/Clock";
import { GridContainer, GridItem, LazyImage } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import loadEventDetails from "@werner/App/Handlers/TrainingCalender/LoadEventDetails";
import { CustomAppState, TrainingCalenderState } from "@werner/App/Store/Reducers";
import ProductAddedToCartMessage from "@werner/Overrides/Components/ProductAddedToCartMessage";
import { IconConfig, productAddToCartButtonStyles } from "@werner/Overrides/Components/ProductAddToCartButton";
import { addToCartStatus } from "@werner/XDL/constants";
import Cart from "@werner/XDL/Icons/CartHeader";
import Document from "@werner/XDL/Icons/Document";
import Star from "@werner/XDL/Icons/Star";
import Success2 from "@werner/XDL/Icons/Success2";
import { color, getSystemColor, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import parse from "html-react-parser";
import React, { useContext, useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    shortDescription = "shortDescription",
    longDescription = "longDescription",
    isFullWidth = "isFullWidth",
    displayRegisterCTA = "displayRegisterCTA",
    registrationFormId = "registrationFormId",
    flyer = "flyer",
    brandImage = "brandImage",
    duration = "duration",
    durationDescription = "durationDescription",
    price = "price",
    priceDescription = "priceDescription",
    topic = "topic",
    eventTags = "eventTags",
    locations = "locations",
    credits = "credits",
    creditDescription = "creditDescription",
    active = "active",
    startDate = "startDate",
    productNumber = "productNumber",
    upcomingDates = "upcomingDates",
}

interface Locations {
    fields: {
        location: string;
    };
}

interface EventTags {
    fields: {
        tag: string;
    };
}

interface UpcomingDate {
    fields: {
        date: string;
        location: string;
        time: string;
        url: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.shortDescription]: string;
        [fields.longDescription]: string;
        [fields.isFullWidth]: boolean;
        [fields.displayRegisterCTA]: boolean;
        [fields.registrationFormId]: string;
        [fields.flyer]: string;
        [fields.brandImage]: string;
        [fields.duration]: string;
        [fields.durationDescription]: string;
        [fields.price]: string;
        [fields.priceDescription]: string;
        [fields.topic]: string;
        [fields.eventTags]: EventTags[];
        [fields.locations]: Locations[];
        [fields.credits]: string;
        [fields.creditDescription]: string;
        [fields.active]: boolean;
        [fields.startDate]: Date;
        [fields.productNumber]: string;
        [fields.upcomingDates]: UpcomingDate[];
    };
}

type Props = PageProps & OwnProps;

export interface TrainingCalenderEventDetailsPageStyles {
    title?: TypographyProps;
    container?: GridContainerProps;
    topSectionContainer?: GridContainerProps;
    longDescription?: TypographyProps;
    rightColumnGridItem?: GridItemProps;
    leftColumnGridItem?: GridItemProps;
    actionsWrapper?: InjectableCss;
    eventPrice?: InjectableCss;
    priceDescription?: TypographyProps;
    eventRegister?: InjectableCss;
    button?: InjectableCss;
    eventInfoSection?: InjectableCss;
    eventCard?: InjectableCss;
    iconStyle?: IconPresentationProps;
    creditsIcon?: IconPresentationProps;
    eventFlyer?: InjectableCss;
    flyerIcon?: IconPresentationProps;
    eventLabel?: InjectableCss;
    eventLabelMainText?: TypographyProps;
    eventLabelSubText?: TypographyProps;
    iconWrapper?: InjectableCss;
    flyerLink?: LinkPresentationProps;
    imgStyle?: LazyImageProps;
    middleSectionGridItem?: GridItemProps;
    addToCartWrapper?: InjectableCss;
    addAllToCartButton?: ButtonPresentationProps;
}
export const trainingCalenderEventDetailsPageStyles: TrainingCalenderEventDetailsPageStyles = {
    title: {
        css: css`
            margin-bottom: 29px;
        `,
    },
    container: {
        css: css``,
    },
    topSectionContainer: {
        css: css`
            padding-bottom: 56px;
        `,
    },
    longDescription: {
        color: "text.main",
        size: 16,
        css: css`
            line-height: 24px;
            margin-bottom: 24px;
            width: 100%;
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            border: 1px solid ${color("primary.main", 0.05)};
            box-shadow: 0 0 15px 0 ${color("primary.main", 0.15)};
            width: 100%;
            max-width: 303px;
            ${xScreen("lte", "md")} {
                max-width: 100%;
            }
        `,
    },
    eventPrice: {
        css: css`
            width: 100%;
            background-color: ${color("common.background")};
            padding: 34px 0 26px 0;
        `,
    },

    priceDescription: {
        as: "strong",
        size: 22,
        css: css`
            line-height: 22px;
        `,
    },
    eventRegister: {
        css: css`
            background-color: ${color("primary.main", 0.05)};
            padding: 20px;
            width: 100%;
            border-bottom: 1px solid ${color("primary.main", 0.05)};
        `,
    },
    button: {
        css: css`
            width: 100%;
        `,
    },
    eventInfoSection: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            padding: 30px;
            background-color: ${color("primary.main", 0.05)};
            ${xScreen("lte", "md")} {
                flex-direction: column;
                row-gap: 10px;
            }
        `,
    },
    eventCard: {
        css: css`
            display: flex;
            border: 1px solid ${color("primary.main", 0.05)};
            box-shadow: 0 0 15px 0 ${color("primary.main", 0.15)};
            border-radius: 3px;
            background-color: ${color("common.white")};
            max-width: 245px;
            max-height: 85px;
            width: 100%;
            height: 100%;
            ${xScreen("lte", "md")} {
                width: 100%;
                max-width: 100%;
            }
        `,
    },
    iconStyle: {
        color: "common.white",
        size: 42,
    },
    eventLabel: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            background-color: white;
            color: black;
            padding: 20px 38px 26px 22px;
            ${xScreen("lte", "md")} {
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                width: 100%;
            }
        `,
    },
    eventLabelMainText: {
        as: "strong",
        size: 15,
        color: "text.main",
        lineHeight: "22px",
        weight: 600,
        css: css`
            ${xScreen("lte", "md")} {
                font-size: 15px;
                line-height: 22px;
                font-weight: 600;
            }
        `,
    },
    eventLabelSubText: {
        as: "span",
        size: 15,
        color: "text.main",
        lineHeight: "22px",
        weight: 400,
        css: css`
            flex-shrink: 0;
        `,
    },
    iconWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: ${color("primary.main")};
            padding: 18px 22px 20px 22px;
        `,
    },
    flyerLink: {
        color: "common.ui3",
        hoverStyle: {
            color: getSystemColor("primary.main"),
        },
        icon: {
            iconProps: {
                src: ChevronRight,
                color: "common.ui3",
                css: css`
                    margin-left: 0;
                `,
            },
            position: "right",
        },
        typographyProps: {
            size: 15,
            weight: 400,
            lineHeight: "22px",
        },
    },
    imgStyle: {
        css: css`
            object-fit: contain;
            max-width: 100%;
            max-height: 100%;
            height: auto;
            margin-top: 50px;
        `,
    },
    middleSectionGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            padding-bottom: 0;
            &&& {
                margin-bottom: 30px;
            }
            &:empty {
                display: none;
            }
        `,
    },
    addToCartWrapper: {
        css: css`
            background-color: ${color("primary.main", 0.05)};
            padding: 20px;
            width: 100%;
        `,
    },
    addAllToCartButton: {
        ...mergeWithCss(productAddToCartButtonStyles, {
            css: css`
                min-width: 200px;
                width: 100%;
            `,
        }),
    },
};
const styles = trainingCalenderEventDetailsPageStyles;
const awaitableAddToCart = makeHandlerChainAwaitable(addToCart);

const TrainingCalenderEventDetailsPage: React.FunctionComponent<Props> = ({ id, fields }) => {
    const dispatch = useAppDispatch();
    const pageId = useAppSelector(state => getCurrentPage(state).id);
    const stickyHeight = useAppSelector(state => (state as CustomAppState).xHeader.stickyHeader || "70");
    const trainingCalender = useAppSelector<TrainingCalenderState>(state => (state as CustomAppState).trainingCalender);
    const { canAddToCart, productId, eventUrl } = trainingCalender;

    const toasterContext = useContext(ToasterContext);

    // id added to the rich content wrapper.
    let registrationForm: HTMLElement | null;
    // id for the iframeContainer added to show/hide iframe
    let iframerContainer: HTMLElement | null;
    // this button is created within rich-content (added to replicate classic behavior)
    let regFormBtn: HTMLElement | null;

    const [status, setStatus] = useState(addToCartStatus.default);

    const iconConfig: IconConfig = {
        src: status === addToCartStatus.success ? Success2 : Cart,
        position: "right",
        ...{ color: "secondary", size: 26 },
    };

    useEffect(() => {
        if (status === addToCartStatus.success) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 800);
        }
    }, [status]);

    useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }
        iframerContainer = document.getElementById("regForm");
        regFormBtn = document.getElementById("regBtn");

        // show/hide form onClick
        if (iframerContainer) {
            regFormBtn?.addEventListener("click", () => {
                iframerContainer!.style.display = iframerContainer!.style.display === "none" ? "block" : "none";
            });
        }
    }, []);

    useEffect(() => {
        if (pageId) {
            dispatch(loadEventDetails({ pageId }));
        }
    }, [dispatch, pageId]);

    const addToCartClickHandler = async () => {
        if (!productId) {
            return;
        }

        setStatus(addToCartStatus.loading);
        try {
            await dispatch(
                awaitableAddToCart({
                    productId,
                    qtyOrdered: 1,
                    unitOfMeasure: "EA",
                    properties: {
                        isEventOrderLine: true,
                        eventId: pageId,
                        eventUrl,
                    },
                } as any) as any,
            );
            setStatus(addToCartStatus.success);

            toasterContext.addToast({
                body: (
                    <ProductAddedToCartMessage
                        isQtyAdjusted={false}
                        multipleProducts={false}
                        qtyExceededInventory={false}
                        isProductEvent={true}
                    />
                ),
                messageType: "success",
            });
        } catch {
            setStatus(addToCartStatus.default);
        }
    };

    const goToRegistrationFormHandler = () => {
        if (!registrationForm) {
            registrationForm = document.getElementById(fields?.registrationFormId);
        }
        if (registrationForm) {
            iframerContainer && (iframerContainer.style.display = "block");
            const registrationFormTop = registrationForm.getBoundingClientRect().top + window.scrollY;
            const scrollPosition = registrationFormTop - parseInt(stickyHeight, 10);

            window.scrollTo({
                top: scrollPosition,
                behavior: "smooth",
            });
        }
    };
    const fullWidth = fields.isFullWidth ? [true, true, true, true, true] : undefined;

    return (
        <Page fullWidth={fullWidth}>
            <StyledWrapper {...styles.title}>
                {fields.title && <Typography variant="h1">{fields.title}</Typography>}
            </StyledWrapper>
            <GridContainer {...styles.container}>
                <GridItem width={12}>
                    <GridContainer {...styles.topSectionContainer}>
                        <GridItem {...styles.leftColumnGridItem}>
                            <Typography {...styles.longDescription}>
                                {parse(fields.longDescription, parserOptions)}
                            </Typography>
                            <StyledWrapper {...styles.eventInfoSection}>
                                {fields.duration && (
                                    <StyledWrapper {...styles.eventCard}>
                                        <StyledWrapper {...styles.iconWrapper}>
                                            <Icon src={Clock} {...styles.iconStyle} />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.eventLabel}>
                                            <Typography {...styles.eventLabelMainText}>{fields.duration}</Typography>
                                            <Typography {...styles.eventLabelSubText}>
                                                {translate("Duration")}
                                            </Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                                {fields.credits && (
                                    <StyledWrapper {...styles.eventCard}>
                                        <StyledWrapper {...styles.iconWrapper}>
                                            <Icon src={Star} {...styles.iconStyle} />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.eventLabel}>
                                            <Typography {...styles.eventLabelMainText}>
                                                {fields.creditDescription}
                                            </Typography>
                                            <Typography {...styles.eventLabelSubText}>
                                                {translate("Credits")}
                                            </Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.eventCard}>
                                    <StyledWrapper {...styles.iconWrapper}>
                                        <Icon src={Document} {...styles.iconStyle} />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.eventLabel}>
                                        <Typography {...styles.eventLabelMainText}>
                                            {translate("Course Flyer")}
                                        </Typography>
                                        <Link
                                            href={fields.flyer}
                                            target="_blank"
                                            aria-label="View Flyer Clickable"
                                            {...styles.flyerLink}
                                        >
                                            <Typography>View Flyer</Typography>
                                        </Link>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.rightColumnGridItem}>
                            <StyledWrapper {...styles.actionsWrapper}>
                                <StyledWrapper {...styles.eventPrice}>
                                    <Typography {...styles.priceDescription}>{fields.priceDescription}</Typography>
                                </StyledWrapper>
                                {fields.displayRegisterCTA && (
                                    <StyledWrapper {...styles.eventRegister}>
                                        <Button
                                            {...styles.button}
                                            onClick={goToRegistrationFormHandler}
                                            aria-label="Registration Form"
                                        >
                                            {translate("Registration Form")}
                                        </Button>
                                    </StyledWrapper>
                                )}

                                {canAddToCart && (
                                    <StyledWrapper {...styles.addToCartWrapper}>
                                        <Button
                                            {...styles.addAllToCartButton}
                                            onClick={addToCartClickHandler}
                                            icon={iconConfig}
                                            data-test-selector="addEventProductToCart"
                                            aria-label="Add to Cart"
                                            disabled={status === addToCartStatus.loading}
                                            {...{ isLoading: status === addToCartStatus.loading }}
                                        >
                                            {translate("Add to Cart")}
                                        </Button>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                            <XngZone contentId={id} zoneName="AdditionalContentNotify" hint="Notify" />
                            {fields.brandImage && (
                                <LazyImage src={fields.brandImage} altText={fields.title} {...styles.imgStyle} />
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.middleSectionGridItem}>
                    <XngZone contentId={id} zoneName="AdditionalContent" hint="Event Content" />
                </GridItem>
                {fields.displayRegisterCTA && (
                    <GridItem width={12}>
                        <XngZone contentId={id} zoneName="RegistrationForm" hint="RegistrationForm" />
                    </GridItem>
                )}
            </GridContainer>
        </Page>
    );
};
const pageModule: PageModule = {
    component: TrainingCalenderEventDetailsPage,
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "Content",
        fieldDefinitions: [
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            HideHeader,
            HorizontalRule,
            MetaDescription,
            MetaKeywords,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                name: fields.title,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },

            {
                name: fields.shortDescription,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.longDescription,
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                isRequired: true,
                extendedConfig: { height: 150 },
            },
            {
                name: fields.isFullWidth,
                displayName: "Is Full Width Page",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: AdvancedTab,
            },
            {
                name: fields.displayRegisterCTA,
                displayName: "Display Register CTA",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
            {
                name: fields.registrationFormId,
                displayName: "RegistrationForm ID",
                defaultValue: "RegistrationForm",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
                tooltip: translate("This ID should be used inside targeted rich content block"),
                isVisible: item => item.fields.displayRegisterCTA === true,
            },
            {
                name: fields.flyer,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.brandImage,
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "Translatable",
            },
            {
                name: fields.duration,
                editorTemplate: "SystemListDropDownField",
                systemListName: "Werner-Duration",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.durationDescription,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
            },
            {
                name: fields.price,
                editorTemplate: "SystemListDropDownField",
                systemListName: "Werner-Price",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.priceDescription,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
            },
            {
                name: fields.topic,
                editorTemplate: "SystemListDropDownField",
                systemListName: "Werner-Topic",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.eventTags,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.tag;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "tag",
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "Werner-Tags",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.locations,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.location;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "location",
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "Werner-Locations",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.credits,
                editorTemplate: "SystemListDropDownField",
                systemListName: "Werner-Credits",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.creditDescription,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "Translatable",
            },
            {
                name: fields.active,
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
            {
                name: fields.startDate,
                defaultValue: new Date(),
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.productNumber,
                displayName: "Product Number",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.upcomingDates,
                displayName: "Upcoming Dates",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    const date = item.fields.date ? new Date(item.fields.date).toDateString() : "";
                    return `${item.fields.location} : ${date}`;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "location",
                        displayName: "Location",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "url",
                        displayName: "Location Link",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "date",
                        displayName: "Date",
                        editorTemplate: "DateTimeField",
                        showTime: false,
                        defaultValue: new Date(),
                        isRequired: true,
                    },
                    {
                        name: "time",
                        displayName: "Time",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};
export default pageModule;
/**
 * @deprecated Use string literal "TrainingCalenderEventDetailsPage" instead of this constant.
 */
export const TrainingCalenderEventDetailsPageContext = "TrainingCalenderEventDetailsPage";
